import type { NlsKey } from '../../nls/strings_nls'
import { getNlsString } from '../../nls/nlsRuntime'
import {
  requestWorkspaceSwitch,
  getLastWorkspaceSwitch,
  type SwitchWorkspaceTargetOptions,
  type WorkspaceSwitchSource,
} from '../workspaceSwitchService'

export const WORKSPACE_DEEP_LINK_TARGET_KINDS = [
  'chat',
  'item',
  'checklist',
  'meeting',
  'form',
  'report',
  'raid',
  'inbox',
] as const

export type WorkspaceDeepLinkTargetKind = (typeof WORKSPACE_DEEP_LINK_TARGET_KINDS)[number]

export interface ParsedWorkspaceDeepLink {
  workspaceId: string
  target: string
  targetKind: WorkspaceDeepLinkTargetKind
  targetId?: string
}

/**
 * Parses /w/{workspaceId}/{target} deep links on the client.
 * Returns null for unmappable or non-/w/ paths.
 */
export function parseWorkspaceDeepLink(path: string | null | undefined): ParsedWorkspaceDeepLink | null {
  if (!path || typeof path !== 'string') return null
  let cleanPath: string
  if (path.includes('://')) {
    try {
      cleanPath = new URL(path).pathname
    } catch {
      return null
    }
  } else {
    cleanPath = path.split(/[?#]/)[0].trim()
  }
  const match = cleanPath.match(/^\/w\/([^/?#]+)\/([^?#]+)/)
  if (!match) return null

  const workspaceId = match[1]
  const target = match[2].replace(/^\/+/, '')
  const firstSegment = target.split('/')[0]

  if (!WORKSPACE_DEEP_LINK_TARGET_KINDS.includes(firstSegment as WorkspaceDeepLinkTargetKind)) {
    return null
  }

  const targetKind = firstSegment as WorkspaceDeepLinkTargetKind
  const targetId = target.split('/')[1] || undefined

  return {
    workspaceId,
    target,
    targetKind,
    targetId,
  }
}

const translateHelper = (
  t: ((key: NlsKey, values?: Record<string, string | number>) => string) | undefined,
  key: NlsKey,
  fallback: string,
  values?: Record<string, string | number>,
): string => {
  if (t) {
    try {
      return t(key, values)
    } catch {
      // fallback
    }
  }
  try {
    return getNlsString(key, values)
  } catch {
    let res = fallback
    if (values) {
      for (const [k, v] of Object.entries(values)) {
        res = res.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v))
      }
    }
    return res
  }
}

export interface FirstSnapshotWaitHandle {
  promise: Promise<boolean>
  cancel: () => void
}

/**
 * Creates a listener for 'timeline:first-snapshot' event for the target workspace,
 * allowing listening to start before an async switch begins.
 */
export function createFirstSnapshotListener(
  workspaceId: string,
  timeoutMs = 4000,
): FirstSnapshotWaitHandle {
  if (typeof window === 'undefined') {
    return {
      promise: Promise.resolve(true),
      cancel: () => {},
    }
  }
  let timer: number | null = null
  let handler: ((event: Event) => void) | null = null

  const cleanup = () => {
    if (timer !== null) {
      window.clearTimeout(timer)
      timer = null
    }
    if (handler !== null) {
      window.removeEventListener('timeline:first-snapshot', handler)
      handler = null
    }
  }

  const promise = new Promise<boolean>((resolve) => {
    handler = (event: Event) => {
      const custom = event as CustomEvent<{ workspaceId?: string }>
      if (custom.detail?.workspaceId === workspaceId) {
        cleanup()
        resolve(true)
      }
    }
    timer = window.setTimeout(() => {
      cleanup()
      resolve(false)
    }, timeoutMs)
    window.addEventListener('timeline:first-snapshot', handler)
  })

  return {
    promise,
    cancel: cleanup,
  }
}

/**
 * Waits for the 'timeline:first-snapshot' event for the target workspace,
 * falling back to timeout (default 4s) so navigation never hangs.
 */
export const waitForFirstSnapshot = (
  workspaceId: string,
  timeoutMs = 4000,
): Promise<boolean> => {
  return createFirstSnapshotListener(workspaceId, timeoutMs).promise
}

function resolveLandingPage(target?: string | null): string | undefined {
  if (!target) return undefined
  if (target.startsWith('/')) return target
  const parts = target.split('/')
  const kind = parts[0]
  const id = parts[1]
  switch (kind) {
    case 'chat':
      return id ? `/chat/${id}` : '/chat'
    case 'item':
    case 'checklist':
      return id ? `/?item=${id}` : '/'
    case 'meeting':
      return id ? `/meetings/${id}` : '/meetings'
    case 'form':
      return id ? `/forms/${id}` : '/forms'
    case 'report':
      return id ? `/reports/${id}` : '/reports'
    case 'raid':
      return id ? `/raid/${id}` : '/raid'
    case 'inbox':
      return '/inbox'
    default:
      return undefined
  }
}

export interface OpenWorkspaceTargetArgs {
  workspaceId: string | null | undefined
  target?: string | null
  source?: WorkspaceSwitchSource
  open?: (() => void | Promise<void>) | null
  options?: SwitchWorkspaceTargetOptions
}

/**
 * Universal cross-workspace target opener (WS4, Track D, R9 S10).
 * Handles:
 * 1. Same-workspace: executes `open` immediately with no toast.
 * 2. Non-member: toasts NLS "You don't have access to that workspace anymore", stops.
 * 3. Other workspace: requests switch with landing page, waits for first-snapshot signal (4s fallback),
 *    executes `open`, and shows "Switched to {name}" toast with Undo (8s).
 */
export async function openWorkspaceTarget({
  workspaceId,
  target,
  source = 'NOTIFICATION',
  open,
  options,
}: OpenWorkspaceTargetArgs): Promise<boolean> {
  const activeWorkspaceId = options?.getActiveWorkspaceId
    ? options.getActiveWorkspaceId()
    : options?.activeWorkspaceId !== undefined
      ? options.activeWorkspaceId
      : null

  const workspaces = options?.getWorkspaces
    ? options.getWorkspaces()
    : options?.workspaces !== undefined
      ? options.workspaces
      : []

  const showToast = options?.showToast
  const t = options?.t

  // Same workspace or no target workspace specified
  if (!workspaceId || workspaceId === activeWorkspaceId) {
    if (open) await open()
    return true
  }

  const showAccessLostToast = () => {
    if (!showToast) return
    if (options?.legacyAccessLostToast) {
      const displayName = options?.workspaceName || workspaceId || ''
      const message = translateHelper(
        t,
        'NLSWorkspaceNoLongerInWorkspace',
        "You're no longer in {name}",
        { name: displayName },
      )
      showToast(message, 'warning')
    } else {
      const accessLostMsg = translateHelper(
        t,
        'NLSWorkspaceNoAccessToast',
        "You don't have access to that workspace anymore",
      )
      showToast(accessLostMsg, 'warning')
    }
  }

  // Membership check: if workspace list is loaded and target not found
  if (workspaces.length > 0 && !workspaces.some((w) => w.id === workspaceId)) {
    showAccessLostToast()
    return false
  }

  const previousId = activeWorkspaceId
  const landing = resolveLandingPage(target)

  // Attach first-snapshot listener before switch to prevent racing cached workspaces
  const snapshotWait = createFirstSnapshotListener(workspaceId, 4000)

  let result
  try {
    result = await requestWorkspaceSwitch({
      toWorkspaceId: workspaceId,
      source,
      landing,
      options,
    })
  } catch (err) {
    snapshotWait.cancel()
    throw err
  }

  if (result === 'NOOP') {
    snapshotWait.cancel()
    if (open) await open()
    return true
  }

  if (result === 'NOT_A_MEMBER') {
    snapshotWait.cancel()
    showAccessLostToast()
    return false
  }

  if (result !== 'SWITCHED') {
    snapshotWait.cancel()
    return false
  }

  // Wait for new workspace's first Timeline snapshot (started listening before switch)
  if (!options?.legacyAccessLostToast) {
    await snapshotWait.promise
  } else {
    snapshotWait.cancel()
  }

  if (open) {
    await open()
  }

  if (showToast && previousId) {
    const targetWs = workspaces.find((w) => w.id === workspaceId)
    const targetName = targetWs?.name || options?.workspaceName || workspaceId
    const switchMsg = translateHelper(
      t,
      'NLSWorkspaceSwitchedToast',
      'Switched to {name}',
      { name: targetName },
    )
    const undoLabel = translateHelper(t, 'NLSToastActionUndo', 'Undo')
    const last = getLastWorkspaceSwitch()
    const undoFromId = last?.fromWorkspaceId ?? previousId
    const undoLanding = last?.fromRoute
    const undoAction = {
      label: undoLabel,
      run: async () => {
        await requestWorkspaceSwitch({
          toWorkspaceId: undoFromId,
          landing: undoLanding,
          source: 'UNDO',
          options,
        })
      },
    }

    if (options?.legacyAccessLostToast) {
      showToast(switchMsg, 'info', undoAction)
    } else {
      showToast(switchMsg, 'info', undoAction, 'normal', { durationMs: 8000 })
    }
  }

  return true
}
